import React from "react";

import { useFrame } from "../frame";
import { usePrefersReducedMotion } from "../reducedMotion";
import { GL_RENDER } from "./config";

export interface GLSize {
    /** Drawing buffer size, device px. */
    width: number;
    height: number;
    /** Canvas box size, CSS px. */
    cssWidth: number;
    cssHeight: number;
    dpr: number;
}

export interface GLCanvasProps<S> {
    className?: string;
    style?: React.CSSProperties;
    /** Cap on devicePixelRatio for the drawing buffer. */
    dprMax?: number;
    /** Merged over the defaults below. Read once, when the context is created. */
    contextAttributes?: WebGLContextAttributes;
    /** How far outside the viewport (px) the canvas keeps drawing. */
    offscreenMargin?: number;
    /** Create programs, targets and buffers. Runs again after a context restore. */
    onInit: (gl: WebGL2RenderingContext, canvas: HTMLCanvasElement) => S;
    /** The drawing buffer has been resized; size render targets here. */
    onResize?: (gl: WebGL2RenderingContext, size: GLSize, state: S) => void;
    /**
     * Draw a frame. `dt` is 0 for a still frame: under reduced motion, and the
     * redraw after a resize.
     */
    onFrame: (
        gl: WebGL2RenderingContext,
        now: number,
        dt: number,
        state: S,
    ) => void;
    /** Delete what onInit created. */
    onDispose?: (gl: WebGL2RenderingContext, state: S) => void;
    /** No WebGL2, `?nogl=1`, or onInit threw. The canvas is not rendered. */
    onUnsupported?: () => void;
}

// Transparent and premultiplied, which is what the section canvases want; the
// hero overrides alpha. No depth, stencil or MSAA: every pass is a fullscreen
// triangle.
const DEFAULT_CONTEXT: WebGLContextAttributes = {
    alpha: true,
    premultipliedAlpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    powerPreference: "high-performance",
};

const noGLRequested = () =>
    new URLSearchParams(window.location.search).get("nogl") === "1";

interface Scene<S> {
    gl: WebGL2RenderingContext;
    state: S;
    size: GLSize | null;
}

/**
 * Hosts one WebGL2 canvas. Owns the context, sizing, the offscreen pause and
 * reduced motion; the scene itself lives in the callbacks. Draws from the
 * page's shared ticker, and only while within `offscreenMargin` of the
 * viewport, so simulation time handed out as `dt` pauses while offscreen.
 */
export function GLCanvas<S>({
    className,
    style,
    dprMax = GL_RENDER.dprMax,
    contextAttributes,
    offscreenMargin = 200,
    ...callbacks
}: GLCanvasProps<S>) {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const sceneRef = React.useRef<Scene<S> | null>(null);
    const callbacksRef = React.useRef(callbacks);
    const dprMaxRef = React.useRef(dprMax);
    const contextRef = React.useRef(contextAttributes);
    const reduced = usePrefersReducedMotion();

    // the effects below are set up once and read the latest props through these
    React.useLayoutEffect(() => {
        callbacksRef.current = callbacks;
        dprMaxRef.current = dprMax;
    });

    const [unsupported, setUnsupported] = React.useState(false);
    const [sized, setSized] = React.useState(false);
    const [near, setNear] = React.useState(false);

    const drawStill = React.useCallback(() => {
        const scene = sceneRef.current;
        if (!scene?.size) return;
        callbacksRef.current.onFrame(
            scene.gl,
            performance.now(),
            0,
            scene.state,
        );
    }, []);

    // context, init, resize, context loss, dispose
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const fail = (error?: unknown) => {
            if (error) console.error(error);
            setUnsupported(true);
            callbacksRef.current.onUnsupported?.();
        };

        const gl = noGLRequested()
            ? null
            : canvas.getContext("webgl2", {
                  ...DEFAULT_CONTEXT,
                  ...contextRef.current,
              });
        if (!gl) {
            fail();
            return;
        }

        let cssWidth = 0;
        let cssHeight = 0;

        const init = () => {
            sceneRef.current = {
                gl,
                state: callbacksRef.current.onInit(gl, canvas),
                size: null,
            };
        };

        const resize = () => {
            const scene = sceneRef.current;
            if (!scene || cssWidth <= 0 || cssHeight <= 0) return;
            const dpr = Math.min(
                window.devicePixelRatio || 1,
                dprMaxRef.current,
            );
            const width = Math.max(1, Math.round(cssWidth * dpr));
            const height = Math.max(1, Math.round(cssHeight * dpr));
            if (canvas.width !== width) canvas.width = width;
            if (canvas.height !== height) canvas.height = height;
            scene.size = { width, height, cssWidth, cssHeight, dpr };
            callbacksRef.current.onResize?.(gl, scene.size, scene.state);
            setSized(true);
            // Resizing clears the drawing buffer, and ResizeObserver runs after
            // this frame's draw, so redraw now or the cleared buffer is painted
            drawStill();
        };

        try {
            init();
        } catch (error) {
            fail(error);
            return;
        }

        const observer = new ResizeObserver((entries) => {
            const box = entries[entries.length - 1].contentRect;
            cssWidth = Math.round(box.width);
            cssHeight = Math.round(box.height);
            resize();
        });
        observer.observe(canvas);

        // moving the window to a screen with another devicePixelRatio keeps
        // the CSS size, so the observer stays quiet; this query flips instead.
        // `resolution` is Safari 16+, the -webkit- feature covers Safari 14-15
        let unwatchDpr = () => {};
        const watchDpr = () => {
            const dpr = window.devicePixelRatio || 1;
            const query = window.matchMedia(
                `(resolution: ${dpr}dppx), (-webkit-device-pixel-ratio: ${dpr})`,
            );
            const onChange = () => {
                watchDpr();
                resize();
            };
            query.addEventListener("change", onChange, { once: true });
            unwatchDpr = () => query.removeEventListener("change", onChange);
        };
        watchDpr();

        const onLost = (event: Event) => {
            // without preventDefault the browser never offers a restore
            event.preventDefault();
            sceneRef.current = null;
            setSized(false);
        };
        const onRestored = () => {
            try {
                init();
            } catch (error) {
                fail(error);
                return;
            }
            resize();
        };
        canvas.addEventListener("webglcontextlost", onLost);
        canvas.addEventListener("webglcontextrestored", onRestored);

        return () => {
            observer.disconnect();
            unwatchDpr();
            canvas.removeEventListener("webglcontextlost", onLost);
            canvas.removeEventListener("webglcontextrestored", onRestored);
            const scene = sceneRef.current;
            sceneRef.current = null;
            setSized(false);
            if (scene && !gl.isContextLost()) {
                callbacksRef.current.onDispose?.(gl, scene.state);
            }
        };
    }, [drawStill]);

    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || unsupported) return;
        const observer = new IntersectionObserver(
            (entries) => setNear(entries[entries.length - 1].isIntersecting),
            { rootMargin: `${offscreenMargin}px 0px` },
        );
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [offscreenMargin, unsupported]);

    // switching reduced motion on mid-animation leaves the current frame up;
    // this redraws it so a still is always a complete frame
    React.useEffect(() => {
        if (reduced && sized) drawStill();
    }, [reduced, sized, drawStill]);

    useFrame(
        (now, dt) => {
            const scene = sceneRef.current;
            if (!scene?.size) return;
            callbacksRef.current.onFrame(scene.gl, now, dt, scene.state);
        },
        sized && near && !reduced,
    );

    if (unsupported) return null;
    return (
        <canvas
            ref={canvasRef}
            className={className}
            style={style}
            aria-hidden="true"
        />
    );
}
